import type { AgendaViewMode } from './useAgendaUrlState'

/**
 * Durable per-user agenda preference (#3319): which agenda the user lives in
 * (personal or combined), whether the combined view follows day-presence
 * ("Aanwezig"), and whether its people picker also offers the administrative
 * staff on the roster (#3596). Unlike the URL view-state — which deliberately
 * resets on a fresh tab so the agenda always opens on today (#3244) — these
 * choices are preferences, not view-state: a planner who works from the
 * combined presence board should land there again tomorrow morning.
 *
 * The stored preference feeds `useAgendaUrlState`'s *defaults*, so an explicit
 * URL param (deep link, back-nav restore) still wins.
 */

export interface AgendaViewPreference {
  mode: AgendaViewMode
  aanwezig: boolean
  /** Whether the combined people picker also lists non-behandelaren (#3596). */
  iedereen: boolean
}

const AGENDA_VIEW_PREFERENCE_KEY = 'scrivio_agenda_view_preference'

const FALLBACK: AgendaViewPreference = { mode: 'personal', aanwezig: false, iedereen: false }

export function loadAgendaViewPreference(): AgendaViewPreference {
  try {
    const raw = localStorage.getItem(AGENDA_VIEW_PREFERENCE_KEY)
    if (!raw) return { ...FALLBACK }
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) return { ...FALLBACK }
    const candidate = parsed as Partial<AgendaViewPreference>
    return {
      mode: candidate.mode === 'combined' ? 'combined' : 'personal',
      aanwezig: candidate.aanwezig === true,
      iedereen: candidate.iedereen === true,
    }
  } catch {
    return { ...FALLBACK }
  }
}

export function saveAgendaViewPreference(preference: AgendaViewPreference): void {
  try {
    localStorage.setItem(AGENDA_VIEW_PREFERENCE_KEY, JSON.stringify(preference))
  } catch {
    // localStorage not available, ignore
  }
}

/**
 * The preference as it was when this page loaded, read once and shared by every
 * `useAgendaUrlState` instance on the page.
 *
 * Instances snapshot their defaults at creation, and a URL param equal to the
 * default is *omitted* from the query. If each instance read the (mutable)
 * preference itself, two instances created before and after a save would omit
 * and resolve the same absent param differently. Freezing the value per page
 * load keeps every instance's defaults identical; a saved change simply takes
 * effect on the next fresh load, which is exactly the "preference" semantic.
 */
let sessionPreference: AgendaViewPreference | null = null

export function sessionAgendaViewPreference(): AgendaViewPreference {
  sessionPreference ??= loadAgendaViewPreference()
  return sessionPreference
}

/** Test-only: forget the per-page-load snapshot. */
export function resetSessionAgendaViewPreferenceForTests(): void {
  sessionPreference = null
}
